import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useCallback, useEffect } from "react";
import { GraduationCap, Plus, Trash2, ChevronDown, ChevronUp, Loader2, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";

type CpdCategory = "clinical" | "professional" | "educational" | "leadership";

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  category: CpdCategory;
  hours: number;
  learningNeed: string;
  activityUndertaken: string;
  outcome: string;
  patientImpact: string;
  createdAt: string;
}

const CATEGORY_LABELS: Record<CpdCategory, string> = {
  clinical: "Clinical",
  professional: "Professional",
  educational: "Educational",
  leadership: "Leadership",
};

const CATEGORY_COLORS: Record<CpdCategory, string> = {
  clinical: "bg-emerald-50 text-emerald-700 border-emerald-200",
  professional: "bg-blue-50 text-blue-700 border-blue-200",
  educational: "bg-violet-50 text-violet-700 border-violet-200",
  leadership: "bg-amber-50 text-amber-700 border-amber-200",
};

const HCPC_STANDARD: Record<CpdCategory, string> = {
  clinical: "HCPC Standard 1 & 2 — Maintaining & developing clinical knowledge",
  professional: "HCPC Standard 3 — Benefiting service users",
  educational: "HCPC Standard 1 — CPD activities range",
  leadership: "HCPC Standard 4 — Practice-relevant learning",
};

const currentYear = new Date().getFullYear();

interface AddFormProps {
  onSaved: () => void;
  onCancel: () => void;
}

function AddActivityForm({ onSaved, onCancel }: AddFormProps) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [category, setCategory] = useState<CpdCategory>("clinical");
  const [hours, setHours] = useState("");
  const [learningNeed, setLearningNeed] = useState("");
  const [activityUndertaken, setActivityUndertaken] = useState("");
  const [outcome, setOutcome] = useState("");
  const [patientImpact, setPatientImpact] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !date || !hours) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/physio/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          date,
          category,
          hours: parseFloat(hours),
          learningNeed: learningNeed.trim(),
          activityUndertaken: activityUndertaken.trim(),
          outcome: outcome.trim(),
          patientImpact: patientImpact.trim(),
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      onSaved();
    } catch {
      setError("Failed to save activity. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-emerald-200 p-6 space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">Add CPD Activity</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label htmlFor="physio-cpd-title" className="block text-xs font-medium text-slate-600 mb-1.5">Activity Title *</label>
          <input
            id="physio-cpd-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. CSP Congress 2026 — Rheumatology Physio Stream"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label htmlFor="physio-cpd-date" className="block text-xs font-medium text-slate-600 mb-1.5">Date *</label>
          <input
            id="physio-cpd-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label htmlFor="physio-cpd-hours" className="block text-xs font-medium text-slate-600 mb-1.5">Hours *</label>
          <input
            id="physio-cpd-hours"
            type="number"
            min="0.5"
            step="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="e.g. 6"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="physio-cpd-category" className="block text-xs font-medium text-slate-600 mb-1.5">Category (HCPC aligned) *</label>
          <select
            id="physio-cpd-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as CpdCategory)}
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="clinical">Clinical — Direct patient care, clinical skills, case-based learning</option>
            <option value="professional">Professional — Governance, ethics, standards, service development</option>
            <option value="educational">Educational — Courses, conferences, webinars, reading, research</option>
            <option value="leadership">Leadership — Management, supervision, service improvement, audit</option>
          </select>
        </div>
        <div className="col-span-2">
          <label htmlFor="physio-cpd-learn-need" className="block text-xs font-medium text-slate-600 mb-1.5">Learning Need Identified *</label>
          <textarea
            id="physio-cpd-learn-need"
            value={learningNeed}
            onChange={(e) => setLearningNeed(e.target.value)}
            rows={2}
            placeholder="What gap or need prompted this CPD activity?"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="physio-cpd-activity" className="block text-xs font-medium text-slate-600 mb-1.5">Learning Activity Undertaken *</label>
          <textarea
            id="physio-cpd-activity"
            value={activityUndertaken}
            onChange={(e) => setActivityUndertaken(e.target.value)}
            rows={2}
            placeholder="Describe the activity, e.g. attended CSP online webinar on axSpA exercise..."
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="physio-cpd-outcome" className="block text-xs font-medium text-slate-600 mb-1.5">Outcome / Impact on Practice *</label>
          <textarea
            id="physio-cpd-outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            rows={2}
            placeholder="What changed in your practice as a result?"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="physio-cpd-patient" className="block text-xs font-medium text-slate-600 mb-1.5">Application to Patient Care *</label>
          <textarea
            id="physio-cpd-patient"
            value={patientImpact}
            onChange={(e) => setPatientImpact(e.target.value)}
            rows={2}
            placeholder="How did this benefit patients or service users?"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

      <div className="flex gap-3">
        <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {saving ? "Saving..." : "Save Activity"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
}

export default function PhysioCpdLog() {
  const [year, setYear] = useState(currentYear);
  const [activities, setActivities] = useState<CpdActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/physio/cpd?year=${year}`);
      if (res.ok) setActivities(await res.json());
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this CPD activity? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/physio/cpd/${id}`, { method: "DELETE" });
      setActivities((prev) => prev.filter((a) => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const totalHours = activities.reduce((s, a) => s + a.hours, 0);
  const byCategory = activities.reduce((acc, a) => {
    acc[a.category] = (acc[a.category] ?? 0) + a.hours;
    return acc;
  }, {} as Record<string, number>);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <PhysioLayout title="CPD Activity Log">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <GraduationCap className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">HCPC CPD Activity Log</h2>
              <p className="text-emerald-300 text-sm">{year} · Aligned with HCPC CPD Standards for Physiotherapists</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-white/10 rounded-xl p-3 col-span-1">
              <p className="text-2xl font-bold">{totalHours.toFixed(1)}</p>
              <p className="text-xs text-emerald-300">Total hours</p>
            </div>
            {(["clinical", "professional", "educational", "leadership"] as CpdCategory[]).map((cat) => (
              <div key={cat} className="bg-white/10 rounded-xl p-3">
                <p className="text-lg font-bold">{(byCategory[cat] ?? 0).toFixed(1)}</p>
                <p className="text-xs text-emerald-300 capitalize">{cat}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <label htmlFor="physio-cpd-year" className="text-sm font-medium text-slate-600">Year:</label>
            <select
              id="physio-cpd-year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2">
            <Plus className="h-4 w-4" /> Add Activity
          </Button>
        </div>

        {showForm && (
          <AddActivityForm onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />
        )}

        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-2xl" />)}</div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <GraduationCap className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No CPD activities logged for {year}</p>
            <p className="text-sm mt-1">Add your first activity to start building your HCPC CPD portfolio.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((a) => {
              const expanded = expandedId === a.id;
              return (
                <div key={a.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedId(expanded ? null : a.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedId(expanded ? null : a.id); }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${CATEGORY_COLORS[a.category]}`}>
                          {CATEGORY_LABELS[a.category]}
                        </span>
                        <span className="text-xs text-slate-400">{HCPC_STANDARD[a.category]}</span>
                      </div>
                      <p className="font-semibold text-slate-900 text-sm">{a.title}</p>
                      <p className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-emerald-700">{a.hours} hrs</p>
                    </div>
                    {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                  </div>
                  {expanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                      {[
                        { label: "Learning Need Identified", value: a.learningNeed },
                        { label: "Activity Undertaken", value: a.activityUndertaken },
                        { label: "Outcome / Impact on Practice", value: a.outcome },
                        { label: "Application to Patient Care", value: a.patientImpact },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
                          <p className="text-sm text-slate-700 leading-relaxed">{value}</p>
                        </div>
                      ))}
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleDelete(a.id)}
                          disabled={deletingId === a.id}
                          className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          {deletingId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>HCPC CPD Standards:</strong> All registered physiotherapists must maintain a CPD profile, undertake a range of CPD activities, apply learning to practice, and be prepared to present a profile at audit. Records here support your HCPC audit submission but do not replace formal registration renewal.
        </div>
      </div>
    </PhysioLayout>
  );
}
